import { Group, Stack } from '@mantine/core';
import { EntityType } from '@repo/common/enums/entityType';
import { useNavigate } from '@repo/common/hooks/useNavigate';
import { Text, Title } from '@repo/foundations';
import { RichEditor } from '../../../../components/RichEditor';
import { buildResourceUrl } from '../../../../utils/navigationUtils';
import type { IResourceContentProps } from '../../types';
import { SchemaTablesDroppedContent } from './SchemaTablesDroppedContent';
import { TableSchemaChangeContent } from './TableSchemaChangeContent';

export function ResourceBasicContent({ selected }: IResourceContentProps) {
	const { entity } = selected;
	const navigate = useNavigate();
	const title = entity?.title ?? selected.resource_title;
	const description = entity?.description ?? '';
	const definition = entity?.definition ?? '';
	const content = selected.content ?? '';

	const isSchemaChangeEvent =
		selected.event === 'table_schema_changed' ||
		selected.event === 'tables_dropped';

	if (!definition && !description && !isSchemaChangeEvent) {
		return null;
	}

	const handleNavigateToResource = () => {
		navigate(
			buildResourceUrl(
				selected.entity
					? {
							...selected.entity,
							...(selected.resource_type === EntityType.reply &&
							typeof selected.metadata?.parent_id === 'string'
								? { parentId: selected.metadata.parent_id as string }
								: {}),
						}
					: {
							id: selected.resource_id,
							entity_type: selected.resource_type,
						}
			)
		);
	};

	return (
		<Stack spacing="2xl">
			{title && (
				<Group
					onClick={handleNavigateToResource}
					pt="xl"
					pb="xs"
					spacing="xs"
					sx={{ cursor: 'pointer' }}
				>
					<Title size="xl">{title}</Title>
				</Group>
			)}
			{description && (
				<Stack spacing="sm">
					<Text size="sm" color="text/secondary/default">
						Description
					</Text>
					<RichEditor readOnly value={description} />
				</Stack>
			)}
			{content && (
				<Stack spacing="sm">
					<Text size="sm" color="text/secondary/default">
						Content
					</Text>
					<RichEditor readOnly value={content} />
				</Stack>
			)}
			{definition && (
				<Stack spacing="sm">
					<Text size="sm" color="text/secondary/default">
						Documentation
					</Text>
					<RichEditor readOnly value={definition} />
				</Stack>
			)}
			{selected.event === 'table_schema_changed' && (
				<TableSchemaChangeContent selected={selected} />
			)}
			{selected.event === 'tables_dropped' && (
				<SchemaTablesDroppedContent selected={selected} />
			)}
		</Stack>
	);
}
